import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { ChipButton } from '@apigrowfinance/pbolt-chip-button';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import GenericLoadingText from '@apigrowfinance/pbolt-generic-loading-text';
import { useQuery } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { CustomersItem } from '@clients/customersClient';
import transactionsClient, {
    TransactionsType,
} from '@clients/transactionsClient';
import TimelineSkeleton from '@components/accountTimeline/skeletons/TimelineSkeleton';
import EndMessage from '@components/infiniteScroll/EndMessage';
import LoadingError from '@components/loadingError/LoadingError';
import TransactionsFilterDialog from '@components/transactions/transactionsFilter/TransactionsFilterModal';
import { useAccount } from '@contexts/AccountProvider';
import useGetCustomers from '@hooks/queries/useGetCustomers';
import useGetTransactions from '@hooks/queries/useGetTransactions';
import useLoadMoreIfNecessary from '@hooks/useLoadMoreIfNecessary';
import transactionTypes from '@messages/transactionTypes';
import TransactionMonthBlock, {
    TransactionMonthBlockProp,
} from '@pages/transactions/TransactionMonthBlock';
import { getYearAndMonth } from '@utils/dateUtil';
import getEnumTranslation from '@utils/getEnumTranslation';
import notifyAPIError from '@utils/notifyAPIError';

const { getTransactionTypes } = transactionsClient();

export type TransactionFilters = {
    customerId?: number;
    eventDateStart?: string;
    eventDateEnd?: string;
    credit?: boolean;
    order?: string;
    authorizationType?: string;
    processingCode?: string;
    transactionTypeId?: number;
    statementPost?: boolean;
    authorizationTrackingId?: string;
    authorizationId?: number;
};

export type FilterOptions = {
    transactionTypes?: TransactionsType[];
    customerOptions?: CustomersItem[];
};

const Transactions = ({ pageSize = 50 }: { pageSize?: number }) => {
    const [filterTransactionsOpen, setFilterTransactionsOpen] = useState(false);
    const [transactionFilters, setTransactionFilters] = useState(
        {} as TransactionFilters,
    );
    const [filterOptions, setFilterOptions] = useState({} as FilterOptions);
    const { program } = useAccount();
    const { accountId } = useParams();
    const programId = program?.id;

    useGetCustomers(parseFloat(accountId), (data) => {
        setFilterOptions({ ...filterOptions, customerOptions: data?.items });
    });

    useQuery(
        ['transactionTypes', programId],
        () => getTransactionTypes({ programId }),
        {
            onSuccess: (data) => {
                setFilterOptions({
                    ...filterOptions,
                    transactionTypes: data?.pagination?.items,
                });
            },
            enabled: !!programId,
            onError: notifyAPIError,
        },
    );

    const { data, isError, isLoading, hasNextPage, fetchNextPage, refetch } =
        useGetTransactions({
            accountId: +accountId,
            pageSize: pageSize,
            ...transactionFilters,
        });

    useLoadMoreIfNecessary({ fetchNextPage, hasNextPage, isLoading }, [
        data?.pages,
    ]);

    useEffect(() => {
        refetch();
    }, [refetch, transactionFilters]);

    const handleClick = () => setFilterTransactionsOpen(true);

    /**
     * Create an array combining all the items from all the pages
     * We start with `transaction = pages[x].items[y]` and want `transaction = transactions[x]`
     * Take the initial pages array and remove any empty entries - in theory there never should be any, but this is
     * just for protection. Map the pages (array of objects) items property (array) into a new 2D array
     * [[items],[items]] then flatten it into just a single array [items] and refer to it as 'transactions'.
     * Default to an empty array if there is nothing
     */
    const transactions =
        data?.pages
            ?.filter(Boolean)
            ?.map((page) => page?.items)
            ?.flat(1) || [];

    const monthBlocks: TransactionMonthBlockProp[] = [];

    transactions?.map((transaction) => {
        if (transaction?.event_date) {
            const yearAndMonth = getYearAndMonth(transaction?.event_date);
            const block = monthBlocks?.find(
                (block) => block.id === yearAndMonth,
            );
            block
                ? block.transactions.push(transaction)
                : monthBlocks.push({
                      id: yearAndMonth,
                      transactions: [transaction],
                      heading: (
                          <DateFormatter
                              format="monthYearFull"
                              date={transaction?.event_date}
                          />
                      ),
                  });
        }
    });

    const getLabel = (filter: [string, string | number | boolean]) => {
        if (filter[0] === 'customerId') {
            const customer = filterOptions?.customerOptions?.filter(
                (customer) => {
                    return customer.customer.id === filter[1];
                },
            );
            return customer[0].customer.printed_name;
        }
        if (filter[0] === 'eventDateStart') {
            return (
                <>
                    <span style={{ marginRight: 2 }}>From:</span>
                    <DateFormatter
                        date={filter[1] as string}
                        format="shortDateAbr"
                    />
                </>
            );
        }

        if (filter[0] === 'eventDateEnd') {
            return (
                <>
                    <span style={{ marginRight: 2 }}>To:</span>
                    <DateFormatter
                        date={filter[1] as string}
                        format="shortDateAbr"
                    />
                </>
            );
        }

        if (filter[0] === 'transactionTypeId') {
            return getEnumTranslation(filter[1] as string, {
                messages: transactionTypes,
            });
        }

        switch (filter[1]) {
            case 'asc':
                return 'Oldest to newest';
            case 'desc':
                return 'Newest to oldest';
            case 'NETWORK':
                return 'Network';
            case 'PLATFORM':
                return 'Platform';
            case true:
                if (filter[0] === 'credit') {
                    return 'Credit';
                } else {
                    return 'Statement Post';
                }
            case false:
                if (filter[0] === 'credit') {
                    return 'Debit';
                } else {
                    return 'No Statement Post';
                }
            default:
                return filter[1];
        }
    };

    if (isError) {
        return (
            <Box sx={{ mt: 10 }}>
                <LoadingError />
            </Box>
        );
    }

    return (
        <>
            <Box>
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end">
                    {Object.entries(transactionFilters).map(function (filter) {
                        if (filter[1] !== undefined) {
                            const filterProperty = filter[0];
                            return (
                                <Box mr={1.25} key={filterProperty}>
                                    <ChipButton
                                        label={getLabel(filter)}
                                        onDelete={() => {
                                            setTransactionFilters({
                                                ...transactionFilters,
                                                [filterProperty]: undefined,
                                            });
                                        }}
                                    />
                                </Box>
                            );
                        }
                    })}
                    <ChipButton
                        onClick={handleClick}
                        label={getEnumTranslation('filter')}
                        variant="filterMenu"
                    />
                </Box>
                {isLoading && <TimelineSkeleton />}
                {!isLoading && (
                    <InfiniteScroll
                        dataLength={transactions?.length}
                        next={fetchNextPage}
                        hasMore={hasNextPage}
                        endMessage={
                            <EndMessage
                                message={
                                    // TODO-jl, make generic message
                                    <FormattedMessage
                                        id="AccountTransactions.noMoreTransactions"
                                        defaultMessage="No more transactions to show"
                                    />
                                }
                            />
                        }
                        loader={
                            <GenericLoadingText
                                displayText={getEnumTranslation('loading')}
                                showLoader
                            />
                        }>
                        {monthBlocks?.map((blockData) => (
                            <TransactionMonthBlock
                                {...blockData}
                                key={blockData?.id}
                            />
                        ))}
                    </InfiniteScroll>
                )}
            </Box>
            {filterTransactionsOpen && (
                <TransactionsFilterDialog
                    filterOptions={filterOptions}
                    transactionFilters={transactionFilters}
                    setTransactionFilters={setTransactionFilters}
                    filterTransactionsOpen={filterTransactionsOpen}
                    setFilterTransactionsOpen={setFilterTransactionsOpen}
                    refetch={refetch}
                />
            )}
        </>
    );
};

export default Transactions;
